'use client';

import { useState, useMemo } from 'react';
import { X, ArrowRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocalizedNavigate } from '@/components/i18n';
import { getUpgradeUrl } from '@/lib/upgrade';
import { useSubscription } from '@/hooks/useSubscription';
import { trackEvent } from '@/lib/posthog';
import { cn } from '@/lib/utils';

const SESSION_DISMISS_KEY = 'settlewise-trial-banner-dismissed';

type Urgency = 'subtle' | 'warning' | 'urgent';

function getUrgency(daysRemaining: number): Urgency {
  if (daysRemaining <= 1) return 'urgent';
  if (daysRemaining <= 4) return 'warning';
  return 'subtle';
}

function getDaysRemaining(trialEnd: string): number {
  const now = new Date();
  const end = new Date(trialEnd);
  const diffMs = end.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

export function TrialBanner() {
  const { subscription, isTrialing } = useSubscription();
  const navigate = useLocalizedNavigate();

  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_DISMISS_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const { daysRemaining, urgency } = useMemo(() => {
    if (!isTrialing || !subscription.trial_end) {
      return { daysRemaining: -1, urgency: 'subtle' as Urgency };
    }
    const days = getDaysRemaining(subscription.trial_end);
    return { daysRemaining: days, urgency: getUrgency(days) };
  }, [isTrialing, subscription.trial_end]);

  // Don't render if not trialing or no trial_end
  if (!isTrialing || !subscription.trial_end) return null;

  // Only allow dismissal for subtle variant
  if (dismissed && urgency === 'subtle') return null;

  const handleUpgrade = () => {
    trackEvent('trial_banner_upgrade_clicked', {
      days_remaining: daysRemaining,
      urgency,
      tier: subscription.tier,
    });
    navigate(getUpgradeUrl('trial_banner'));
  };

  const handleDismiss = () => {
    trackEvent('trial_banner_dismissed', {
      days_remaining: daysRemaining,
      urgency,
    });
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, 'true');
    } catch {
      // sessionStorage unavailable
    }
    setDismissed(true);
  };

  const styles = {
    subtle: {
      container: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/50',
      text: 'text-blue-800 dark:text-blue-200',
      icon: 'text-blue-500 dark:text-blue-400',
      button: 'border-blue-300 text-blue-800 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-200 dark:hover:bg-blue-900',
      dismiss: 'text-blue-500 hover:bg-blue-100 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-900',
    },
    warning: {
      container: 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50',
      text: 'text-amber-800 dark:text-amber-200',
      icon: 'text-amber-500 dark:text-amber-400',
      button: 'border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900',
      dismiss: '',
    },
    urgent: {
      container: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/50',
      text: 'text-red-800 dark:text-red-200',
      icon: 'text-red-500 dark:text-red-400',
      button: 'bg-red-600 text-white hover:bg-red-700 border-red-600 dark:bg-red-700 dark:hover:bg-red-800',
      dismiss: '',
    },
  };

  const s = styles[urgency];

  const message = urgency === 'urgent'
    ? 'Trial expires today \u2014 upgrade now to keep access'
    : urgency === 'warning'
      ? `Your trial ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} \u2014 upgrade to keep access`
      : `Free trial \u00b7 ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining`;

  return (
    <div className={cn('flex items-center gap-3 mx-4 mt-2 rounded-lg border px-4 py-2.5', s.container)}>
      <Clock className={cn('h-4 w-4 shrink-0', s.icon)} />

      <p className={cn('flex-1 text-sm font-medium', s.text)}>
        {message}
      </p>

      <Button
        variant="outline"
        size="sm"
        onClick={handleUpgrade}
        className={cn('shrink-0', s.button)}
      >
        Upgrade
        <ArrowRight className="ml-1 h-3.5 w-3.5" />
      </Button>

      {urgency === 'subtle' && (
        <button
          onClick={handleDismiss}
          className={cn('shrink-0 rounded p-1', s.dismiss)}
          aria-label="Dismiss trial banner"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export default TrialBanner;
